import { Location } from "@angular/common";
import { inject, Injectable, OnDestroy, DOCUMENT } from "@angular/core";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";

import { filter, Subscription, tap } from "rxjs";

import {
    Context,
    INFRA_SETTINGS,
    LOCALE,
    REQUEST,
    RESPONSE,
    WINDOW,
} from "@hermes/core-app-core";
import { InfraSettings } from "@hermes/core-env-infra";
import { redirectResponse } from "@hermes/core-response-service";

import { initPreviousUrlFromReferrer } from "./init-previous-url";

const isPageReload = (): boolean => {
    const [navEntry] = performance.getEntriesByType(
        "navigation",
    ) as PerformanceNavigationTiming[];

    return navEntry && navEntry.type === "reload";
};
@Injectable({
    providedIn: "root",
})
export class UrlUtils implements OnDestroy {
    private context = inject(Context);
    private window = inject<Window>(WINDOW);
    private request = inject(REQUEST);
    private response = inject(RESPONSE);
    private locale = inject(LOCALE);
    private infraSettings = inject<InfraSettings>(INFRA_SETTINGS);
    private document = inject(DOCUMENT);
    private router = inject(Router);
    private location = inject(Location);

    private previousUrl: string;
    private currentUrl: string;
    private subscription: Subscription = new Subscription();

    constructor() {
        if (this.context.isInServerMode()) {
            this.previousUrl = this.request?.headers?.referer ?? "";
            this.currentUrl = this.request?.url ?? "";
            return;
        }
        this.currentUrl = this.location.path();
        this.previousUrl = isPageReload()
            ? this.location.path()
            : initPreviousUrlFromReferrer({
                  document: this.document,
                  infraSettings: this.infraSettings,
                  locale: this.locale,
              });

        this.subscription.add(
            this.router.events
                .pipe(
                    filter(
                        (event) =>
                            event instanceof NavigationStart ||
                            event instanceof NavigationEnd,
                    ),
                    tap((event) => {
                        if (this.currentUrl === event.url) {
                            return;
                        }

                        if (event instanceof NavigationStart) {
                            this.previousUrl = this.currentUrl;
                        }
                        if (event instanceof NavigationEnd) {
                            this.currentUrl = event.urlAfterRedirects;
                        }
                    }),
                )
                .subscribe(),
        );
    }

    /**
     * Retrieves the previous URL based on the application's history or document referrer.
     *
     * @returns The previous URL or empty string if not available.
     */
    public getPreviousUrl(): string {
        // Previous url will be relative if it comes from an internal url
        if (this.previousUrl.length > 0 && !this.previousUrl.includes("http")) {
            return `${this.infraSettings.frontend}${this.locale.urlPrefix}${this.previousUrl}`;
        }
        return this.previousUrl;
    }

    /**
     * Return previous relative url
     */
    public getPreviousRelativeUrl(): string {
        return this.previousUrl.includes("http") ? "/" : this.previousUrl;
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /*
     * Allow to redirect to an external url
     */
    public redirectToExternalUrl(url: string): void {
        if (this.context.isInBrowserMode()) {
            this.changeLocation(url);
        }
        if (this.context.isInServerMode()) {
            redirectResponse({
                request: this.request,
                response: this.response,
                newPath: url,
                statusCode: 302,
            });
        }
    }

    /*
     * Allow to redirect to an internal url
     */
    public redirectToInternalUrl(path: string): void {
        if (this.context.isInBrowserMode()) {
            this.changeLocation(`${this.locale.urlPrefix}/${path}`);
        }
    }

    /*
     * Allow to redirect to root path with country && language code
     */
    public redirectToRootPath(): void {
        this.redirectToExternalUrl(`${this.locale.urlPrefix}/`);
    }

    /**
     * Redirects to a specified resource with country and language code.
     *
     * @param resource The name of the resource for redirection
     * @param pathParam Optional. Additional path parameter, used as part of the URL if provided
     */
    public redirectToPage(resource: string, pathParameter?: string): void {
        const path = pathParameter ? `${resource}/${pathParameter}` : resource;
        this.redirectToExternalUrl(`${this.locale.urlPrefix}/${path}/`);
    }

    /**
     * change window.location
     */
    public changeLocation(url: string): void {
        this.window.location.href = url;
    }

    public replaceHistory(url: string): void {
        this.window.history.replaceState({}, "", url);
    }

    public getCurrentUrl(): string {
        const currentURL = `${this.infraSettings.frontend}${
            this.locale.urlPrefix
        }${this.location.path().split("?")[0]}`;
        return decodeURI(currentURL);
    }

    /**
     * Return current relative url whithout locale
     */
    public getCurrentRelativeUrl(): string {
        return this.currentUrl;
    }

    /**
     * Return current relative url with locale
     */
    public getCurrentRelativeUrlWithLocale(): string {
        return this.locale.urlPrefix + this.currentUrl;
    }
}
